/* eslint-disable @typescript-eslint/no-explicit-any */
import { ExpiredLinkPage, OnboardingFormChatPanel, OnboardingMediumPage } from "gsintegrations";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  trackOnboardingError,
  trackOnboardingEvent,
  trackOnboardingProductEvent,
} from "../analytics/onboarding";
import { resolvePageKeyFromPathname } from "../analytics/pageRegistry";
import {
  trackSignupEvent,
  type SignupAnalyticsContext,
} from "../analytics/signup";
import { ROUTE } from "../constants/routes";
import {
  ONBOARDING_CHAT_BOT_SUBTITLE,
  ONBOARDING_CHAT_BOT_TITLE,
} from "../constants/onboardingChatBot.metadata";
import { useCachedAuthProfile } from "../hooks/useCachedAuthProfile";
import useMediumSignupMutation, {
  type MediumSignupError,
} from "../hooks/useMediumSignupMutation";
import { useOnboardingMediumQueries } from "../hooks/useOnboardingMediumQueries";
import useQuickMediumSignupMutation from "../hooks/useQuickMediumSignupMutation";
import useQuickMediumSocialSignupMutation from "../hooks/useQuickMediumSocialSignupMutation";
import { useSignupAnalyticsContext } from "../hooks/useSignupAnalyticsContext";
import {
  buildProductBaseFromRegistry,
  useProductAnalyticsContext,
} from "../hooks/useProductAnalyticsContext";
import { qrcodeInviteRepository } from "../repositories/qrcodeInviteRepository";
import {
  sanitizeMediumSignupPayload,
  sanitizeQuickMediumSignupPayload,
  sanitizeQuickMediumSocialSignupPayload,
} from "../sanitizers";
import type {
  MediumOnboardingFormState,
  MediumSignupPayload,
  QuickMediumSignupPayload,
  QuickMediumSocialSignupPayload,
} from "../types";
import {
  resolveSignupAttributionCode,
  sanitizeInternalReturnTo,
} from "../utils/authQueryParams";
import { inviteCodeStorage } from "../utils/inviteCodeStorage";
import { useOnboardingFormChat } from "../hooks/useOnboardingFormChat";
import type { OnboardingChatFormState } from "../repositories/onboardingFormChat.repository";
import { buildMediumOnboardingFormStateFromChat } from "../utils/onboardingFormChatPayloadBuilder";

// OnboardingMode type
type OnboardingMode = "quick" | "complete";

export default function OnboardingMedium() {
  const navigate = useNavigate();
  const location = useLocation();
  const fallbackSignupAnalyticsContext = useSignupAnalyticsContext();
  const productAnalyticsContext = useProductAnalyticsContext();
  const authProfile = useCachedAuthProfile();
  const signupData = location.state as {
    firstName?: string;
    lastName?: string;
    email?: string;
    cellphone?: string;
    socialAuth?: boolean;
    code?: string;
    signupAnalyticsContext?: SignupAnalyticsContext;
  } | null;

  const signupAnalyticsContext = useMemo(
    () => ({
      ...fallbackSignupAnalyticsContext,
      ...(signupData?.signupAnalyticsContext ?? {}),
      page_url: fallbackSignupAnalyticsContext.page_url,
      context_timestamp: fallbackSignupAnalyticsContext.context_timestamp,
    }),
    [fallbackSignupAnalyticsContext, signupData?.signupAnalyticsContext],
  );

  const productBase = useMemo(() => {
    const registryEntry = resolvePageKeyFromPathname(location.pathname);
    return registryEntry
      ? buildProductBaseFromRegistry(
          registryEntry,
          productAnalyticsContext,
          location,
        )
      : {
          pageKey: "onboarding_medium",
          routePath: location.pathname,
          sourcePage: "OnboardingMedium",
          productArea: "onboarding" as const,
          activeProfileType: productAnalyticsContext.activeProfileType,
          terreiroId: productAnalyticsContext.terreiroId,
          mediumId: productAnalyticsContext.mediumId,
        };
  }, [location, productAnalyticsContext]);

  const INITIAL_DATA: MediumOnboardingFormState = {
    firstName: signupData?.firstName ?? "",
    lastName: signupData?.lastName ?? "",
    email: signupData?.email ?? "",
    phone: signupData?.cellphone ?? "",
    birthDate: "",
    initiationDate: "",
    contact: "",
    documentType: "",
    document: "",
    rankId: "",
    roleId: "",
    street: "",
    number: "",
    complement: "",
    city: "",
    state: "",
    neighborhood: "",
    zipCode: "",
    password: "",
    confirmPassword: "",
    guardianName: "",
    guardianPhone: "",
    guardianRelationship: "",
  };

  const [expired, setExpired] = useState(false);
  const [signupError, setSignupError] = useState<
    { code?: string; message: string } | undefined
  >(undefined);
  const [hasSignupSuccess, setHasSignupSuccess] = useState(false);
  const [didRedirectHome, setDidRedirectHome] = useState(false);
  const [searchParams] = useSearchParams();
  const returnTo = sanitizeInternalReturnTo(
    searchParams.get("returnTo"),
    ROUTE.APP_HOME,
  );
  const inviteCode = searchParams.get("inviteCode");
  const signupCode = resolveSignupAttributionCode(
    searchParams,
    signupData?.code,
  );

  // Get onboarding mode from URL param, default to "quick" for faster signup
  const modeParam = searchParams.get("mode") as OnboardingMode | null;
  const mode: OnboardingMode = modeParam === "complete" ? "complete" : "quick";

  // In quick mode, we don't need ranks and roles (they require auth)
  const isQuickMode = mode === "quick";
  const { ranks, roles } = useOnboardingMediumQueries({
    enabled: !isQuickMode,
  });

  const { mutateAsync: completeSignup } = useMediumSignupMutation();
  const { mutateAsync: quickSignup } = useQuickMediumSignupMutation();
  const { mutateAsync: quickSocialSignup } =
    useQuickMediumSocialSignupMutation();

  const navigateHome = useCallback(() => {
    setDidRedirectHome(true);
    trackOnboardingProductEvent("onboarding_completed", productBase, {
      role: "medium",
      onboarding_mode: mode,
      trigger: "mutation",
    });
    trackSignupEvent("signup_redirect_to_home", signupAnalyticsContext, {
      role: "medium",
      onboarding_mode: mode,
    });
    // Cleanup any stale invite markers to avoid post-signup redirect loops.
    inviteCodeStorage.clear();
    inviteCodeStorage.clearInviteOriginMarker();
    navigate(returnTo, { replace: true });
  }, [mode, navigate, productBase, returnTo, signupAnalyticsContext]);

  useEffect(
    () => () => {
      if (hasSignupSuccess && !didRedirectHome) {
        trackSignupEvent(
          "signup_abandon_after_success",
          signupAnalyticsContext,
          {
            role: "medium",
            onboarding_mode: mode,
          },
        );
      }
    },
    [hasSignupSuccess, didRedirectHome, signupAnalyticsContext, mode],
  );

  useEffect(() => {
    if (!inviteCode) {
      // No invite code provided - allow direct signup
      setExpired(false);
      return;
    }

    // Invite code provided - validate it
    qrcodeInviteRepository
      .resolveInvite(inviteCode)
      .then(() => {
        setExpired(false);
      })
      .catch((err) => {
        console.error("Failed to fetch invite:", err);
        setExpired(true);
      });
  }, [inviteCode]);

  const handleOnComplete = async (
    data: MediumOnboardingFormState,
  ): Promise<boolean> => {
    setSignupError(undefined);

    trackSignupEvent(
      "signup_onboarding_submit_attempt",
      signupAnalyticsContext,
      {
        role: "medium",
        onboarding_mode: mode,
        has_invite_code: !!inviteCode,
      },
    );

    // Exclude any code field from form data to avoid empty strings
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { code: _excludedCode, ...dataWithoutCode } = data as any;

    const shouldUseSocialSignup =
      Boolean(signupData?.socialAuth) ||
      (mode === "quick" &&
        !inviteCode &&
        !dataWithoutCode.password &&
        !dataWithoutCode.confirmPassword);

    try {
      if (mode === "quick" || inviteCode) {
        // Quick mode (or invite flow) - use quick signup endpoint with minimal fields
        const quickPayload: QuickMediumSignupPayload = {
          ...dataWithoutCode,
          ...(inviteCode &&
            inviteCode.trim() !== "" && {
              inviteCode: inviteCode.trim(),
              isInviteFlow: true,
            }),
          ...(signupCode &&
            signupCode.trim() !== "" && {
              signupCode: signupCode.trim(),
            }),
        };
        if (shouldUseSocialSignup) {
          const socialPayload: QuickMediumSocialSignupPayload = {
            ...dataWithoutCode,
            ...(inviteCode &&
              inviteCode.trim() !== "" && {
                inviteCode: inviteCode.trim(),
                isInviteFlow: true,
              }),
            ...(signupCode &&
              signupCode.trim() !== "" && {
                signupCode: signupCode.trim(),
              }),
          };
          await quickSocialSignup(
            sanitizeQuickMediumSocialSignupPayload(socialPayload),
          );
        } else {
          await quickSignup(sanitizeQuickMediumSignupPayload(quickPayload));
        }
      } else {
        // Complete mode - use standard signup endpoint with all fields
        if (signupData?.socialAuth) {
          const socialPayload: QuickMediumSocialSignupPayload = {
            ...dataWithoutCode,
            ...(inviteCode &&
              inviteCode.trim() !== "" && {
                inviteCode: inviteCode.trim(),
                isInviteFlow: true,
              }),
            ...(signupCode &&
              signupCode.trim() !== "" && {
                signupCode: signupCode.trim(),
              }),
          };
          await quickSocialSignup(
            sanitizeQuickMediumSocialSignupPayload(socialPayload),
          );
        } else {
          const payload: MediumSignupPayload = {
            ...dataWithoutCode,
            ...(signupCode &&
              signupCode.trim() !== "" && {
                signupCode: signupCode.trim(),
              }),
          };
          await completeSignup(sanitizeMediumSignupPayload(payload));
        }
      }

      setHasSignupSuccess(true);
      trackOnboardingEvent(
        "onboarding_medium_added",
        {
          sourcePage: "OnboardingMedium",
          activeProfileType: "personal",
          trigger: "mutation",
        },
        {
          onboarding_mode: mode,
          has_invite_code: !!inviteCode,
        },
      );
      trackSignupEvent("signup_success", signupAnalyticsContext, {
        role: "medium",
        onboarding_mode: mode,
        has_invite_code: !!inviteCode,
      });
      setSignupError(undefined);
      navigateHome();
      return true;
    } catch (error) {
      const normalizedError: MediumSignupError =
        error && typeof error === "object" && "message" in error
          ? (error as MediumSignupError)
          : { message: String(error) };
      const message = normalizedError.message ?? String(error);

      trackOnboardingError(
        "onboarding_medium_added",
        {
          sourcePage: "OnboardingMedium",
          activeProfileType: "personal",
          trigger: "mutation",
        },
        error,
        {
          onboarding_mode: mode,
          has_invite_code: !!inviteCode,
        },
      );

      trackSignupEvent("signup_error", signupAnalyticsContext, {
        role: "medium",
        onboarding_mode: mode,
        has_invite_code: !!inviteCode,
        error_message: message,
      });

      setSignupError({
        code: normalizedError.code,
        message,
      });
      return false;
    }
  };

  const handleOnCancel = () => {
    if (signupData?.socialAuth) {
      navigate(ROUTE.SIGNUP_TYPE, {
        state: {
          firstName: signupData?.firstName,
          lastName: signupData?.lastName,
          email: signupData?.email,
          code: signupCode,
        },
      });
      return;
    }
    navigate("/signup");
  };

  const referenceData = useMemo(
    () => ({
      ranks:
        mode === "complete"
          ? ranks.map((rank) => ({ id: rank.id, name: rank.name }))
          : undefined,
      roles:
        mode === "complete"
          ? roles.map((role) => ({ id: role.id, name: role.name }))
          : undefined,
    }),
    [mode, ranks, roles],
  );

  const prefilledData = useMemo(
    () => ({
      firstName: signupData?.firstName ?? "",
      lastName: signupData?.lastName ?? "",
      email: signupData?.email ?? "",
      phone: signupData?.cellphone ?? "",
    }),
    [signupData],
  );

  const handleOnCompleteRef = useRef(handleOnComplete);
  handleOnCompleteRef.current = handleOnComplete;

  const handleChatConfirmed = useCallback(
    async (formState: OnboardingChatFormState) => {
      return handleOnCompleteRef.current(
        buildMediumOnboardingFormStateFromChat(formState),
      );
    },
    [],
  );

  const formChat = useOnboardingFormChat({
    entityType: "medium_onboarding",
    mode,
    isSocialAuth: Boolean(signupData?.socialAuth),
    referenceData,
    prefilledData,
    signupAnalyticsContext,
    onConfirmed: handleChatConfirmed,
  });

  if (expired) {
    return (
      <ExpiredLinkPage
        dirigentes={[]}
        onClose={() => {
          navigate("/signup");
        }}
      />
    );
  }

  if (formChat.isFeatureEnabled) {
    return (
      <OnboardingFormChatPanel
        entityType="medium_onboarding"
        title={ONBOARDING_CHAT_BOT_TITLE}
        subtitle={ONBOARDING_CHAT_BOT_SUBTITLE}
        messages={formChat.messages}
        quickReplies={formChat.quickReplies}
        summary={formChat.summary}
        currentStep={formChat.currentStep}
        stepLabels={formChat.stepLabels}
        stepOrder={formChat.stepOrder}
        isLoading={formChat.isLoading}
        error={
          signupError
            ? {
                code: signupError.code ?? "SIGNUP_ERROR",
                message: signupError.message,
              }
            : formChat.error
        }
        confirmLabel="Criar conta"
        voiceEnabled={false}
        activeField={formChat.activeField}
        fieldInput={formChat.fieldInput}
        fieldOptions={formChat.fieldOptions}
        selectedOption={formChat.selectedOption}
        userAvatarUrl={authProfile?.avatarUrl}
        onTranscribeAudio={undefined}
        onSendMessage={formChat.handleSendMessage}
        onSelectOption={formChat.handleSelectOption}
        onConfirm={formChat.handleConfirm}
        onEdit={formChat.handleEdit}
        onCancelEdit={formChat.handleCancelEdit}
        onEditField={formChat.handleEditField}
        editInteraction={formChat.editInteraction}
        editingFieldLabel={formChat.editingFieldLabel}
        editableFieldOptions={formChat.editableFieldOptions}
        onReset={formChat.resetConversation}
        onClose={handleOnCancel}
      />
    );
  }

  return (
    <OnboardingMediumPage
      mode={mode}
      isSocialAuth={Boolean(signupData?.socialAuth)}
      initialData={INITIAL_DATA}
      ranks={mode === "quick" ? [] : ranks}
      roles={mode === "quick" ? [] : roles}
      onComplete={handleOnComplete}
      onCancel={handleOnCancel}
      error={signupError?.message}
    />
  );
}
